import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLang } from '../context/LangContext';
import { getPostSummaries } from '../data/blogPosts';
import { getTeamMemberBySlug } from '../data/teamData';
import { getCoverImageAlt } from './BlogDetail';
// Cover images: map slug → imported image (images live in src/assets)
import Blog1 from '../assets/images/Blog1.jpg';
import blog2 from '../assets/images/blog2.jpg';
import blog3 from '../assets/images/blog3.jpg';
import blog4 from '../assets/images/blog4.jpg';
import blog6 from '../assets/images/blog6.jpg';
import picosureImg from '../assets/images/picosure.jpg';
import pigmentationImg from '../assets/images/pigmentation-guide.jpg';
import potenzaCoverImg from '../assets/images/potenza-microneedling.jpg';
import blog9 from '../assets/images/blog9.png';

const coverBySlug = {
  'beginner-medical-aesthetics': Blog1,
  'skinbooster-cocktail': blog2,
  'hyaluronic-acid-filler': blog2,
  'skin-care-right-way': blog3,
  'post-treatment-care-tips': blog4,
  'radiesse': Blog1,
  'plla-dilution-and-applications': Blog1,
  'sculptra-vs-radiesse': blog4,
  'ipl-vs-picosecond': picosureImg,
  'botox-dilution-and-concentration': blog6,
  'sun-spots-melasma-post-acne-marks': pigmentationImg,
  'potenza-vs-traditional-microneedling': potenzaCoverImg,
  'acne-scars-vs-acne-marks': blog9,
};

/** UI-only strings (no blog post content) */
const content = {
  en: {
    pageTitle: 'Blog',
    metaTitle: 'Skin & Aesthetic Treatment Blog | Halo Richmond Hill',
    pageDesc: 'Professional medical aesthetics knowledge sharing to help you understand medical aesthetics.',
    metaDesc: 'Expert skin & aesthetic treatment guides, injectable & laser therapy insights, post-treatment care tips, and professional skincare knowledge from Halo Aesthetic Clinic in Richmond Hill.',
    categories: [
      { id: 'all', name: 'All' },
      { id: 'knowledge', name: 'Aesthetics Knowledge' },
      { id: 'injection', name: 'Injection Beauty' },
      { id: 'skin', name: 'Skin Care' },
    ],
    author: 'Author',
    readMore: 'Read More →',
    prev: 'Previous',
    next: 'Next',
  },
  cn: {
    pageTitle: '博客资讯',
    metaTitle: '皮肤与医美知识博客 | Halo Richmond Hill 医美诊所',
    pageDesc: '专业的医美知识分享，助您了解医美',
    metaDesc: '专业的医美与皮肤护理博客，提供微整形注射、光电疗程解析、对比指南及术后修复技巧。',
    categories: [
      { id: 'all', name: '全部' },
      { id: 'knowledge', name: '医美知识' },
      { id: 'injection', name: '注射美容' },
      { id: 'skin', name: '皮肤护理' },
    ],
    author: '作者',
    readMore: '阅读更多 →',
    prev: '上一页',
    next: '下一页',
  }
};

const Blog = () => {
  const { lang } = useLang();
  const c = content[lang];
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const POSTS_PER_PAGE = 6;

  // List page: frontmatter only (no body)
  const posts = getPostSummaries(lang);

  const filteredPosts = selectedCategory === 'all'
    ? posts
    : posts.filter((post) => post.category === c.categories.find((cat) => cat.id === selectedCategory)?.name);

  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const currentPosts = filteredPosts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  const handleCategoryChange = (catId) => {
    setSelectedCategory(catId);
    setCurrentPage(1);
  };

  const canonicalUrl = 'https://www.haloaestheticclinic.com/blog';

  return (
    <div className="bg-softGradient bg-no-repeat bg-top text-text">
      <Helmet>
        <title>{c.metaTitle}</title>
        <meta name="description" content={c.metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={c.metaTitle} />
        <meta property="og:description" content={c.metaDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-section">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 text-gray-900">{c.pageTitle}</h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">{c.pageDesc}</p>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:flex sm:justify-center sm:space-x-4 mb-12 max-w-md mx-auto sm:max-w-none">
          {c.categories.map((category) => (
            <button
              key={category.id}
              onClick={() => handleCategoryChange(category.id)}
              className={`px-3 py-2 sm:px-4 sm:py-2 rounded-xl transition-all text-sm sm:text-base ${
                selectedCategory === category.id
                  ? 'text-gray-900 shadow-soft hover:shadow-glow hover:brightness-110 border border-champagne/60'
                  : 'bg-white/60 backdrop-blur-md border border-sage/50 text-gray-700 hover:shadow-glow'
              }`}
              style={selectedCategory === category.id ? {
                backgroundImage:
                  'linear-gradient(90deg, rgba(217,195,169,1) 0%, rgba(217,195,169,0.8) 40%, rgba(217,195,169,0.3) 100%)'
              } : {}}
            >
              {category.name}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {currentPosts.map((post) => {
            const authorKey = post.authorPerson || post.author || 'Lydia';
            const authorMember = getTeamMemberBySlug(authorKey);
            const authorDisplayName = post.authorPerson || authorMember?.name[lang] || post.author;

            return (
              <article
                key={post.slug}
                className="bg-white/80 backdrop-blur-sm rounded-xl shadow-card overflow-hidden border border-champagne/40 flex flex-col justify-between"
              >
                <div>
                  <img
                    src={coverBySlug[post.slug] || post.coverImage}
                    alt={getCoverImageAlt(post, lang)}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <div className="flex items-center space-x-4 mb-4">
                      <span className="text-sm text-[#2E6B4C] font-semibold">{post.category}</span>
                      <span className="text-sm text-gray-600">{post.date}</span>
                    </div>
                    <h2 className="text-xl font-semibold mb-2 text-gray-900">{post.title}</h2>
                    <p className="text-gray-700 mb-4">{post.excerpt}</p>
                  </div>
                </div>
                <div className="px-6 pb-6 pt-0 flex items-center justify-between">
                  <div className="text-sm text-gray-600 flex items-center gap-1">
                    <span>{c.author}：</span>
                    {authorMember && authorMember.hasDetailPage ? (
                      <Link
                        to={`/team/${authorMember.slug}`}
                        aria-label={`View ${authorMember.name[lang] || authorMember.name.en}'s profile`}
                        className="text-[#2E6B4C] font-semibold hover:underline"
                      >
                        {authorDisplayName}
                      </Link>
                    ) : (
                      <Link to="/about" className="text-[#2E6B4C] font-semibold hover:underline">
                        {authorDisplayName}
                      </Link>
                    )}
                  </div>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="text-[#2E6B4C] hover:text-[#2E6B4C]/80 font-semibold transition-colors"
                  >
                    {c.readMore}
                  </Link>
                </div>
              </article>
            );
          })}
        </div>

        {totalPages > 1 && (
          <div className="mt-12 flex justify-center">
            <nav className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-xl border transition-all ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white/60 backdrop-blur-md border border-sage/50 text-gray-700 hover:shadow-glow'
                }`}
              >
                {c.prev}
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-1 rounded-xl transition-all ${
                    currentPage === pageNum
                      ? 'text-gray-900 shadow-soft hover:shadow-glow hover:brightness-110 border border-champagne/60'
                      : 'bg-white/60 backdrop-blur-md border border-sage/50 text-gray-700 hover:shadow-glow'
                  }`}
                  style={
                    currentPage === pageNum
                      ? {
                          backgroundImage:
                            'linear-gradient(90deg, rgba(217,195,169,1) 0%, rgba(217,195,169,0.8) 40%, rgba(217,195,169,0.3) 100%)',
                        }
                      : {}
                  }
                >
                  {pageNum}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded-xl border transition-all ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white/60 backdrop-blur-md border border-sage/50 text-gray-700 hover:shadow-glow'
                }`}
              >
                {c.next}
              </button>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
